/**
 * Render capture → generic node-graph adapter (plans/render-graph-view.md phase 2).
 *
 * Turns one captured frame ({@link RenderCaptureResult}) into a {@link NodeGraph}
 * with `domain: "rendergraph"` — the graph of what made that frame:
 *
 * - **Nodes**: one *pass* node per render call, plus the resources that took
 *   part — scenes (single collapsed node), cameras, renderers, render targets
 *   and the canvas.
 * - **Flow edges** (first real consumer of the model's second edge kind):
 *   pass → pass in execution order — the "each step along the way" spine.
 * - **Data edges**: scene/camera/renderer feeding each pass, and each pass
 *   writing its active target. Targets expose a `texture` output socket for
 *   later read-edge phases (WebGPU pass introspection), unconnected in v1 —
 *   texture *reads* are not observable from render/setRenderTarget alone.
 *
 * Resource graph-nodes carry their {@link CapturedResource} in `meta.resource`
 * (primitive snapshots + weak `deref()`), which is what later phases use to
 * preview a target or jump into a pass's camera.
 */

import type { WebGLRenderer, WebGLRenderTarget } from "three";
import { describeRenderPass } from "../common/passNames.js";
import { ratePassGpuMs } from "../../../editor/services/performance/common/performanceRatings.js";
import type { GpuTimingResult, PassSummary } from "../common/gpuTiming.js";
import {
    createGraph,
    type DataType,
    type GraphNode,
    type NodeGraph,
    type Socket,
} from "../../nodegraph/model.js";
import type { GraphAdapter } from "../../nodegraph/adapter.js";
import { readbackRenderTargetToUrl } from "./helper.readback.js";
import { RenderCapture, type CapturedRenderCall, type CapturedResource, type RenderCaptureOptions, type RenderCaptureResult } from "./renderCapture.js";
import { isRenderer } from "./helper.js";

type ThumbnailFrameScheduler = (callback: () => void) => void;

/** Serializes GPU readbacks and puts every item in a separate rendering frame. */
export class ThumbnailReadbackQueue {
    private tail: Promise<void> = Promise.resolve();

    constructor(private readonly scheduleFrame: ThumbnailFrameScheduler) {}

    enqueue<T>(work: () => Promise<T>, scheduleFrame = this.scheduleFrame): Promise<T> {
        const result = this.tail
            .then(() => new Promise<void>(resolve => scheduleFrame(resolve)))
            .then(work);
        this.tail = result.then(() => undefined, () => undefined);
        return result;
    }
}

const thumbnailReadbacks = new ThumbnailReadbackQueue(callback => {
    if (typeof globalThis.requestAnimationFrame === "function") {
        globalThis.requestAnimationFrame(() => callback());
    } else {
        globalThis.setTimeout(callback, 0);
    }
});

// #region Data types

/** Rendergraph socket value types — all opaque references. */
const REF_TYPES = {
    scene: { signature: "scene", category: "ref" },
    camera: { signature: "camera", category: "ref" },
    renderer: { signature: "renderer", category: "ref" },
    texture: { signature: "texture", category: "ref" },
} satisfies Record<string, DataType>;

// #region Adapter

export interface FrameToGraphSource {
    capture: RenderCaptureResult;
    /** Which captured frame to graph. Default 0. */
    frameIndex?: number;
    /** The last GPU measurement, when there is one — puts each pass's cost on its card. */
    timing?: GpuTimingResult | null;
    /** Display refresh, for rating that cost against the frame budget. */
    targetFps?: number;
}

/**
 * A shared resource feeding more than this many passes is split into per-pass
 * alias nodes (short local edges instead of cross-canvas fan-out spaghetti).
 */
const ALIAS_FAN_OUT_THRESHOLD = 2;

/** Converts one captured frame into the generic node-graph model. */
/**
 * Which measured pass belongs to which captured render call.
 *
 * Three rules, weakest last. BY SCENE first: the measurement records the uuid of
 * the scene each pass drew, which is the one identity both samples share (the
 * same scene twice = in call order). BY POSITION when there is no scene id but
 * both samples agree on how many passes ran. BY NAME last and only for a name
 * unique on both sides — names collide constantly (six unnamed scenes all read
 * "Scene", two fullscreen quads both read "Custom Shader"), and matching those by
 * name handed every one of them the first match's number.
 *
 * No match means the card shows no time, which beats a wrong one.
 */
export function matchPassTimings(
    calls: readonly CapturedRenderCall[],
    capture: RenderCaptureResult,
    timing?: GpuTimingResult | null,
): Map<number, PassSummary> {
    const out = new Map<number, PassSummary>();
    if (!timing || timing.passes.length === 0) return out;

    // shadows are their own bucket in the measurement, not part of the numbering
    const shadowPass = timing.passes.find(p => p.label === "Shadows");
    const measured = timing.passes.filter(p => p !== shadowPass).slice().sort((a, b) => a.pass - b.pass);
    const rendered = calls.map((call, index) => ({ call, index })).filter(e => e.call.kind !== "shadow");

    for (const { call, index } of calls.map((call, index) => ({ call, index }))) {
        if (call.kind === "shadow" && shadowPass) out.set(index, shadowPass);
    }
    // by scene: `call.scene` is the resource id, which IS the scene's uuid
    const byScene = new Map<string, PassSummary[]>();
    for (const pass of measured) {
        if (!pass.sceneId) continue;
        byScene.set(pass.sceneId, [...(byScene.get(pass.sceneId) ?? []), pass]);
    }
    if (byScene.size > 0) {
        // Partial is fine — the samples genuinely do not cover the same work (our
        // own renders, e.g. the thumbnail readback for these cards, are measured
        // as the editor's and captured as a pass). Shifting the queue means each
        // measurement is handed out once, so a pass we never measured stays blank
        // instead of borrowing someone else's number.
        for (const entry of rendered) {
            const pass = byScene.get(entry.call.scene)?.shift();
            if (pass) out.set(entry.index, pass);
        }
        return out;
    }

    if (rendered.length === measured.length) {
        rendered.forEach((entry, ordinal) => out.set(entry.index, measured[ordinal]));
        return out;
    }

    const nameOf = (call: CapturedRenderCall) =>
        describeRenderPass(capture.resources.get(call.scene)?.deref()) ?? "Render Call";
    const countBy = (names: readonly string[]) =>
        names.reduce((m, n) => m.set(n, (m.get(n) ?? 0) + 1), new Map<string, number>());
    const measuredNames = countBy(measured.map(p => p.label));
    const renderedNames = countBy(rendered.map(e => nameOf(e.call)));
    for (const entry of rendered) {
        const name = nameOf(entry.call);
        if (measuredNames.get(name) !== 1 || renderedNames.get(name) !== 1) continue;
        const match = measured.find(p => p.label === name);
        if (match) out.set(entry.index, match);
    }
    return out;
}

export function frameToGraph(
    capture: RenderCaptureResult,
    frameIndex = 0,
    timing?: GpuTimingResult | null,
    /** the display's frame budget, for rating a pass's cost. 60 Hz when unknown. */
    targetFps = 60,
): NodeGraph {
    const frame = capture.frames[frameIndex];
    const graph = createGraph(`frame-${frameIndex}`, "rendergraph", `Frame ${frameIndex + 1}`, "RenderFrame");
    if (!frame) return graph;

    // With a single renderer every pass would get an identical, information-free
    // edge from the same node — the dominant source of edge spaghetti. Omit the
    // renderer entirely then (it's implicit); keep it only when renderers differ.
    const rendererIds = new Set(frame.calls.map((call) => call.renderer));
    const showRenderer = rendererIds.size > 1;

    // Lazy pass-output thumbnails ("view this pass"): the readback runs only
    // when a card actually shows it, and once per TARGET — every pass/target
    // card sharing a target shares one readback promise. `refresh` re-reads
    // the (live, possibly re-rendered) target — the card does this when its
    // node is selected. Works on WebGL and WebGPU alike (helper.readback
    // handles both). Canvas outputs have no readable target — they get no
    // thumbnail (the page itself shows them).
    const thumbnails = new Map<string, ThumbnailProduce | null>();
    const thumbnailFor = (targetId: string, rendererId: string): ThumbnailProduce | null => {
        let produce = thumbnails.get(targetId);
        if (produce !== undefined) return produce;
        const target = capture.resources.get(targetId);
        const renderer = capture.resources.get(rendererId);
        if (target?.kind !== "renderTarget" || !renderer) {
            thumbnails.set(targetId, produce = null);
            return produce;
        }
        let cached: Promise<string | null> | null = null;
        produce = (options) => {
            if (options?.refresh) cached = null;
            return cached ??= thumbnailReadbacks.enqueue(
                () => readTargetThumbnail(target, renderer),
                callback => scheduleRendererFrame(renderer, callback),
            );
        };
        thumbnails.set(targetId, produce);
        return produce;
    };

    // Resource nodes are created lazily — only resources this frame touched.
    const resourceNodes = new Map<string, GraphNode>();
    const nodeForResource = (id: string): GraphNode => {
        let node = resourceNodes.get(id);
        if (node) return node;
        const resource = capture.resources.get(id);
        node = createResourceNode(id, resource);
        resourceNodes.set(id, node);
        graph.nodes.push(node);
        return node;
    };

    let previousPassId: string | null = null;
    const timedPass = matchPassTimings(frame.calls, capture, timing);

    frame.calls.forEach((call, index) => {
        const passId = `pass${index}`;
        const passName = describeRenderPass(capture.resources.get(call.scene)?.deref()) ?? "Render Call";

        const inputs: Socket[] = [
            { id: "in", name: "in", kind: "flow" },
            { id: "scene", name: "scene", kind: "data", type: REF_TYPES.scene },
            { id: "camera", name: "camera", kind: "data", type: REF_TYPES.camera },
        ];
        if (showRenderer) inputs.push({ id: "renderer", name: "renderer", kind: "data", type: REF_TYPES.renderer });

        const pass: GraphNode = {
            id: passId,
            typeRef: "rendergraph/pass",
            // What the pass DRAWS, when we can tell: an unnamed scene holding one
            // fullscreen quad is a post effect, and its material names it far better
            // than "Render Call" did. Six identical titles carry no information.
            // NOTE `call.scene` is a resource ID — the live object comes from the
            // captured resource, transiently (never held; see CapturedResource).
            // "Pass: " prefix because the name alone collides with the RESOURCE
            // nodes beside it — a pass drawing the RTT scene and the RTT target
            // itself both read "RTT" otherwise.
            title: call.kind === "shadow" ? "Pass: Shadow Maps" : `Pass: ${passName}`,
            category: "pass",
            inputs,
            outputs: [
                { id: "next", name: "", kind: "flow" },
                { id: "out", name: "out", kind: "data", type: REF_TYPES.texture },
            ],
            meta: {
                order: call.order,
                depth: call.depth,
                // shadow passes run INSIDE a render call and are the usual answer to
                // "why is this frame expensive" — worth telling apart in the UI
                ...(call.kind ? { kind: call.kind } : {}),
                renderer: call.renderer,
                // Docs for the render() method of the renderer that issued this call.
                docsUrl: `${rendererDocsPage(capture.resources.get(call.renderer))}.render`,
                // Drill target: the scene this call rendered (same object the
                // connected scene node holds) — lets the pass step into that
                // scene's material graphs. For a postprocessing pass this is the
                // fullscreen-quad scene, so stepping in reveals the effect shader.
                stepResource: capture.resources.get(call.scene),
                tooltip: call.kind === "shadow"
                    ? `three's shadow-map pass: every shadow-casting light re-renders the casters into its`
                      + ` depth map. Drawn INSIDE the render call above it, so it never appears as a`
                      + ` render() call of its own.`
                    :
                    `One renderer.render(scene, camera) call recorded during the captured frame` +
                    ` — #${call.order + 1} in execution order.` +
                    (call.depth > 0
                        ? " Issued from inside another render call (e.g. a postprocessing/composer pass)."
                        : ""),
            },
        };

        // Render-list OVERVIEW: high-level object counts per bucket on the pass
        // card (no per-item capture — a drill-in list is a later phase). The
        // value row reads the tally; structured counts ride the meta for AI.
        const rl = call.renderList;
        if (rl) {
            pass.meta!.renderList = { opaque: rl.opaque, transmissive: rl.transmissive, transparent: rl.transparent, total: rl.total };
            // Always name the buckets that drew (even a single one) — "2 draws"
            // alone doesn't say WHAT; "2 opaque" does.
            const buckets = [
                rl.opaque ? `${rl.opaque} opaque` : null,
                rl.transmissive ? `${rl.transmissive} transmissive` : null,
                rl.transparent ? `${rl.transparent} transparent` : null,
            ].filter(Boolean) as string[];
            pass.meta!.value = buckets.length ? buckets.join(", ") : `${rl.total} draws`;
            pass.meta!.tooltip += `\nDrew ${rl.total} object${rl.total === 1 ? "" : "s"}`
                + (buckets.length ? ` — ${buckets.join(", ")}.` : ".");
        }
        // What it COST, when a measurement exists (see matchPassTimings).
        const measured = timedPass.get(index);
        if (measured) {
            pass.meta!.gpuMs = measured.avgMs;
            // "0.00 ms" reads as free or unmeasured; a 4-microsecond blit is neither
            const time = measured.avgMs < 0.01 ? "<0.01 ms" : `${measured.avgMs.toFixed(2)} ms`;
            // its own field, not glued into the value line: the card gives the
            // measurement a chip, the way every other number in the UI is shown
            pass.meta!.gpuLabel = time;
            pass.meta!.gpuRating = ratePassGpuMs(measured.avgMs, targetFps);
            pass.meta!.tooltip += `
GPU: ${time} on average over ${measured.frames} measured frame`
                + `${measured.frames === 1 ? "" : "s"} (draw calls only).`;
        }
        graph.nodes.push(pass);

        // Resources feeding the pass.
        const feeds: readonly (readonly [string, string])[] = showRenderer
            ? [["scene", call.scene], ["camera", call.camera], ["renderer", call.renderer]]
            : [["scene", call.scene], ["camera", call.camera]];
        for (const [socket, resourceId] of feeds) {
            nodeForResource(resourceId);
            graph.dataEdges.push({
                from: { node: resourceId, socket: "out" },
                to: { node: passId, socket },
            });
        }

        // "View this pass": the pass's output is its target's contents. Note
        // the thumbnail reads the target's CURRENT contents — for a target
        // written by several passes (ping-pong) that is the LAST write.
        const thumbnail = thumbnailFor(call.target, call.renderer);
        if (thumbnail) {
            pass.meta!.mediaValue = true;
            pass.meta!.thumbnail = thumbnail;
            // The live target, so clicking the thumbnail can inspect its texture.
            pass.meta!.outputResource = capture.resources.get(call.target);
            // Aspect lets the card/layout size the thumb (width-capped) without
            // waiting for the image to load.
            const targetResource = capture.resources.get(call.target);
            if (targetResource?.width && targetResource.height) {
                pass.meta!.thumbnailAspect = targetResource.width / targetResource.height;
            }
        }

        // The pass writes its active target. Each write gets its own input
        // socket on the target so repeated writes (ping-pong) stay valid
        // single-fan-in data edges.
        // The target card shows NO thumbnail HERE: in this graph its writing
        // pass always sits right next to it showing the same image, and the
        // duplicate reads as noise. (Elsewhere — a material/TSL graph, where a
        // render target appears as a texture with no pass in sight — showing it
        // IS the useful thing; that belongs to those adapters, not here.)
        const target = nodeForResource(call.target);
        const writeSocket: Socket = {
            id: `write${countInputs(target)}`,
            name: "write",
            kind: "data",
            type: REF_TYPES.texture,
        };
        target.inputs.push(writeSocket);
        graph.dataEdges.push({
            from: { node: passId, socket: "out" },
            to: { node: target.id, socket: writeSocket.id },
        });

        // Execution order: the frame's flow spine.
        if (previousPassId) {
            graph.flowEdges.push({
                from: { node: previousPassId, socket: "next" },
                to: { node: passId, socket: "in" },
            });
        }
        previousPassId = passId;
    });

    // Passes sharing one target (ping-pong / composer reuse): their thumbnail
    // and any reveal show the target's FINAL contents, not that pass's own
    // write — surface it so the mismatch reads as reuse, not as a wrong image
    // (confirmed live on next.junni.co.jp: renderTarget#6 written by pass10
    // AND pass12 — clicking pass10 "showed the wrong texture").
    for (const node of graph.nodes) {
        if (node.category !== "target" || node.inputs.length < 2) continue;
        const writes = graph.dataEdges.filter((e) => e.to.node === node.id);
        writes.forEach((edge, index) => {
            const pass = graph.nodes.find((n) => n.id === edge.from.node);
            if (pass?.meta) {
                pass.meta.targetWrites = writes.length;
                pass.meta.targetWriteIndex = index;
            }
        });
    }

    splitHighFanOutResources(graph);
    return graph;
}

/** Thumbnail producer stashed on `meta.thumbnail` — see {@link frameToGraph}. */
export type ThumbnailProduce = (options?: { refresh?: boolean }) => Promise<string | null>;

function scheduleRendererFrame(renderer: CapturedResource, callback: () => void): void {
    const live = renderer.deref() as WebGLRenderer | undefined;
    const host = live?.domElement?.ownerDocument?.defaultView;
    if (host) host.requestAnimationFrame(() => callback());
    else if (typeof globalThis.requestAnimationFrame === "function") globalThis.requestAnimationFrame(() => callback());
    else globalThis.setTimeout(callback, 0);
}

/**
 * Reads a captured render target's current contents back as a data URL.
 * Transient derefs only (weak-registry rule); every failure — collected
 * refs, depth-only/half-float targets, context loss — resolves to null
 * (card shows "no preview") instead of throwing.
 */
async function readTargetThumbnail(target: CapturedResource, renderer: CapturedResource): Promise<string | null> {
    try {
        const rt = target.deref();
        const owner = renderer.deref();
        if (!rt || !owner) return null;
        // Render-target contents are linear (three encodes only the default
        // framebuffer) — encode for display. HDR values clamp; fine for a thumb.
        // The helper accepts both classic WebGLRenderer and the r182 Renderer
        // (WebGPU) — the parameter types are just the older names.
        // forceOpaque "ifAlphaEmpty": intermediate targets (bloom mips & co.)
        // carry content in RGB with alpha 0 — respecting that would show an empty
        // checkerboard, so lift them opaque; but a pass output that genuinely
        // uses alpha keeps its transparency instead of a solid-black thumbnail.
        // rows auto: row order depends on what WROTE the target (WebGPU scene
        // renders are projection-flipped, TSL postprocessing blits are not) —
        // the backend default is right for the common case per backend.
        return await readbackRenderTargetToUrl(rt as WebGLRenderTarget, owner as WebGLRenderer, null,
            { encodeSRGB: true, maxSize: 256, forceOpaque: "ifAlphaEmpty" });
    } catch {
        return null;
    }
}

/**
 * Splits shared source resources (scene/camera/renderer) that feed more than
 * {@link ALIAS_FAN_OUT_THRESHOLD} passes into one alias node per consumer — the
 * node-editor "get node" pattern. Aliases keep the original `meta.resource`
 * (selection/deref still work) and carry `aliasOf` + `shared` so the view can
 * badge them (e.g. "×8").
 */
function splitHighFanOutResources(graph: NodeGraph): void {
    const sourceKinds = new Set(["scene", "camera", "renderer"]);
    for (const node of [...graph.nodes]) {
        if (!sourceKinds.has(node.category ?? "")) continue;
        const outgoing = graph.dataEdges.filter((e) => e.from.node === node.id);
        if (outgoing.length <= ALIAS_FAN_OUT_THRESHOLD) continue;

        outgoing.forEach((edge, k) => {
            const aliasId = `${node.id}@${k}`;
            graph.nodes.push({
                ...node,
                id: aliasId,
                inputs: [],
                outputs: node.outputs.map((s) => ({ ...s })),
                meta: { ...node.meta, aliasOf: node.id, shared: outgoing.length },
            });
            edge.from = { node: aliasId, socket: edge.from.socket };
        });
        graph.nodes.splice(graph.nodes.indexOf(node), 1);
    }
}

/** The rendergraph {@link GraphAdapter}. */
export const renderGraphAdapter: GraphAdapter<FrameToGraphSource> = {
    domain: "rendergraph",
    toGraph: ({ capture, frameIndex, timing, targetFps }) => frameToGraph(capture, frameIndex ?? 0, timing, targetFps),
};

// #region Selection → capture

/** Renderers among the given selection owners (deduped, duck-typed). */
export function collectRenderers(targets: unknown): object[] {
    const list = Array.isArray(targets) ? targets : [targets];
    const renderers: object[] = [];
    for (const target of list) {
        if (isRenderer(target) && !renderers.includes(target)) renderers.push(target);
    }
    return renderers;
}

/**
 * Captures the next frame of the given renderers and returns it as a graph.
 * The renderer nodes in the result are tagged `meta.selected` so the view can
 * mirror the selection that triggered the capture.
 */
export async function captureRenderGraph(
    renderers: readonly object[],
    options?: RenderCaptureOptions,
    // a THUNK, because a capture waits for the next frame: a measurement taken
    // while it waits belongs on the graph it produces, not the one before it
    timing?: GpuTimingResult | null | (() => GpuTimingResult | null),
    targetFps?: number,
): Promise<NodeGraph> {
    const capture = await new RenderCapture(options).capture(renderers);
    return frameResultToGraph(capture, renderers, typeof timing === "function" ? timing() : timing, targetFps);
}

/**
 * Builds the graph for a captured/observed frame and tags the renderer nodes
 * matching the given (selected) renderers — shared by the demanded capture and
 * the standing last-frame observer.
 */
export function frameResultToGraph(
    capture: RenderCaptureResult,
    selectedRenderers: readonly object[],
    timing?: GpuTimingResult | null,
    targetFps?: number,
): NodeGraph {
    const graph = frameToGraph(capture, 0, timing, targetFps);
    for (const node of graph.nodes) {
        const resource = node.meta?.resource as CapturedResource | undefined;
        if (resource?.kind !== "renderer") continue;
        const live = resource.deref();
        if (live && selectedRenderers.includes(live)) node.meta!.selected = true;
    }
    return graph;
}

// #region Resource nodes

const THREE_DOCS = "https://threejs.org/docs/#api/en";

/** Docs page for the renderer behind a resource (WebGPU vs classic WebGL). */
function rendererDocsPage(resource: CapturedResource | undefined): string {
    const live = resource?.deref?.() as { isWebGPURenderer?: boolean } | undefined;
    return `${THREE_DOCS}/renderers/${live?.isWebGPURenderer ? "WebGPURenderer" : "WebGLRenderer"}`;
}

/** The three.js docs page for a captured resource, when one exists. */
function resourceDocsUrl(resource: CapturedResource | undefined): string | undefined {
    switch (resource?.kind) {
        case "scene":
            return `${THREE_DOCS}/scenes/Scene`;
        case "camera": {
            const type = resource.type;
            const known = type === "PerspectiveCamera" || type === "OrthographicCamera"
                || type === "CubeCamera" || type === "ArrayCamera";
            return `${THREE_DOCS}/cameras/${known ? type : "Camera"}`;
        }
        case "renderer":
            return rendererDocsPage(resource);
        case "renderTarget":
            return `${THREE_DOCS}/renderers/WebGLRenderTarget`;
        case "canvas":
            // Not a three.js type — the canvas is the DOM element the renderer draws to.
            return "https://developer.mozilla.org/en-US/docs/Web/API/HTMLCanvasElement";
        default:
            return undefined;
    }
}

function createResourceNode(id: string, resource: CapturedResource | undefined): GraphNode {
    const kind = resource?.kind ?? "renderTarget";
    const docsUrl = resourceDocsUrl(resource);
    const base: GraphNode = {
        id,
        typeRef: `rendergraph/${kind}`,
        title: resourceTitle(resource),
        category: kind === "renderTarget" ? "target" : kind,
        inputs: [],
        outputs: [],
        meta: resource ? { resource, ...(docsUrl ? { docsUrl } : {}) } : undefined,
    };

    switch (kind) {
        case "scene":
            base.outputs.push({ id: "out", name: "out", kind: "data", type: REF_TYPES.scene });
            break;
        case "camera":
            base.outputs.push({ id: "out", name: "out", kind: "data", type: REF_TYPES.camera });
            break;
        case "renderer":
            base.outputs.push({ id: "out", name: "out", kind: "data", type: REF_TYPES.renderer });
            break;
        case "renderTarget":
            // Write sockets are appended per writing pass; the texture output is
            // where future read edges (pass inputs sampling this target) attach.
            base.outputs.push({ id: "texture", name: "texture", kind: "data", type: REF_TYPES.texture });
            break;
        case "canvas":
            // Final output — writes only.
            break;
    }
    return base;
}

function resourceTitle(resource: CapturedResource | undefined): string {
    if (!resource) return "Unknown";
    if (resource.kind === "canvas") return "Canvas";
    if (resource.kind === "renderTarget") {
        const size = resource.width && resource.height ? `${resource.width}×${resource.height}` : undefined;
        // Keep BOTH when a target is named: the name says which target this is
        // (it comes off the texture — see registerResource), the size is the
        // number people scan a render graph for.
        if (resource.name) return size ? `${resource.name} ${size}` : resource.name;
        return size ? `Target ${size}` : "Render Target";
    }
    return resource.name || resource.type || resource.kind;
}

function countInputs(node: GraphNode): number {
    return node.inputs.length;
}
